import { create } from "zustand";
import { AxiosError } from "axios";
import { APIErrorContext } from "../../../utils/universalAPIErrorHandler";

/**
 * Surfaces a failed /monitoring-api/alert/ fetch on the Alerts page.
 *
 * Background (SUP-445): the alert-list fetchers hand every non-auth failure
 * to `universalAPIErrorHandler`, whose fallback just flips `alertsToDisplay`
 * off — so a 400/5xx from the backend rendered as "No alerts to display",
 * indistinguishable from a genuinely empty result. Max Security's Resolved
 * queue 400'd for a day before anyone could tell. This store lets the fetch
 * utilities record the failure without threading yet another prop through
 * the 60-argument fetch signatures, and lets the table render it.
 */
export interface AlertsFetchError {
  /** HTTP status, or null when the request never got a response. */
  status: number | null;
  /** Short, human-readable summary of the response body (may be empty). */
  detail: string;
  /** Epoch ms when the failure was recorded. */
  at: number;
}

interface AlertsFetchErrorState {
  fetchError: AlertsFetchError | null;
  setFetchError: (error: AlertsFetchError | null) => void;
  clearFetchError: () => void;
}

export const useAlertsFetchErrorStore = create<AlertsFetchErrorState>(
  (set) => ({
    fetchError: null,
    setFetchError: (fetchError) => set({ fetchError }),
    clearFetchError: () => set({ fetchError: null }),
  })
);

const MAX_DETAIL_LENGTH = 200;

/**
 * Reduce an error response body to one short line. DRF shapes handled:
 * `{"detail": "..."}`, a field-error object, and the `many=True` list form
 * (`[{}, {}, {"field": ["msg"]}]`) where only the non-empty entries matter.
 */
export const summarizeErrorBody = (data: unknown): string => {
  if (data === null || data === undefined) return "";
  if (typeof data === "string") return data.slice(0, MAX_DETAIL_LENGTH);
  if (Array.isArray(data)) {
    const firstError = data.find(
      (entry) =>
        entry && typeof entry === "object" && Object.keys(entry).length > 0
    );
    return firstError ? summarizeErrorBody(firstError) : "";
  }
  if (typeof data === "object") {
    const record = data as Record<string, unknown>;
    if (typeof record.detail === "string") {
      return record.detail.slice(0, MAX_DETAIL_LENGTH);
    }
    try {
      return JSON.stringify(record).slice(0, MAX_DETAIL_LENGTH);
    } catch {
      return "";
    }
  }
  return String(data).slice(0, MAX_DETAIL_LENGTH);
};

/**
 * Record a failed alert fetch. Call from the `errorHandlingFunction` passed to
 * `universalAPIErrorHandler`, which only fires for terminal failures (the
 * handler owns the 401/403 token-refresh retry itself). Pass the handler's
 * context through so a 429 that is about to be retried isn't shown as an error.
 */
export const recordAlertsFetchError = (
  error: unknown,
  context?: APIErrorContext
): void => {
  if (context?.willRetry) return;
  const axiosError = error as AxiosError | undefined;
  const status = axiosError?.response?.status ?? null;
  const detail = context?.offline
    ? "You appear to be offline."
    : summarizeErrorBody(axiosError?.response?.data);
  useAlertsFetchErrorStore.getState().setFetchError({
    status,
    detail,
    at: Date.now(),
  });
};

export const clearAlertsFetchError = (): void => {
  useAlertsFetchErrorStore.getState().clearFetchError();
};
